import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiError } from 'shared/types';
import { PlansPurchaseResponse, PurchasePlan } from './plans-purchase.types';

type StoreCallbacks = {
   onError?: (message: string) => void;
   onAuthError?: () => void;
};

export class FeaturePlansPurchaseStore {
   readonly loadPlansState = new AsyncState<PlansPurchaseResponse>();

   plans: PurchasePlan[] = [];

   constructor() {
      makeObservable(this, {
         plans: observable,
         loading: computed,
         hasPlans: computed,
         loadPlans: action.bound,
      });
   }

   get loading(): boolean {
      return this.loadPlansState.status === 'pending';
   }

   get hasPlans(): boolean {
      return this.plans.length > 0;
   }

   async loadPlans({ onError, onAuthError }: StoreCallbacks = {}): Promise<void> {
      await this.loadPlansState.run(() => HttpClient.post<PlansPurchaseResponse>('/plans/purchase', null), {
         onSuccess: (resp) => {
            const plans = resp.response?.plan;

            if (Array.isArray(plans)) {
               runInAction(() => {
                  this.plans = plans;
               });
               return;
            }

            if (this.isAuthMessage(resp.message)) {
               onAuthError?.();
               return;
            }

            onError?.(resp.message || 'Could not get membership plan data');
         },
         onError: (error) => {
            if (this.shouldTreatAsAuthError(error)) {
               onAuthError?.();
               return;
            }

            onError?.(error.message);
         },
      });
   }

   private isAuthMessage(message?: string): boolean {
      return !!message && /token has expired/i.test(message);
   }

   private shouldTreatAsAuthError(error: ApiError): boolean {
      return this.isAuthMessage(error.message) || error.message === 'No response from server';
   }
}
